import { requireAdmin } from "@/lib/admin-guard";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMembership } from "@/hooks/useMembership";
import {
  adminListMemberships,
  adminCancelMembership,
  createCustomerPortalSession,
  createFoundingCheckoutSession,
} from "@/lib/membership.functions";
import { reconcileMembershipTiers } from "@/lib/admin-extra.functions";
import { MemberInsightsPanel } from "@/components/admin/MemberInsightsPanel";
import { CheckoutCampaignPanel } from "@/components/admin/CheckoutCampaignPanel";
import {
  Loader2,
  ShieldAlert,
  ExternalLink,
  RefreshCw,
  Ban,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";

export const Route = createFileRoute("/admin_/membership")({
  ssr: false,
  beforeLoad: requireAdmin,
  head: () => ({
    meta: [
      { title: "Admin — Membership Console (Recovery Trauma™)" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminMembershipPage,
});

type Row = Awaited<ReturnType<typeof adminListMemberships>>[number];

const STATUS_LABEL: Record<string, string> = {
  pending_activation: "Pending Activation",
  active: "Active",
  cancelling: "Cancelling · no renewal",
  payment_failed: "Payment Failed",
  cancelled: "Cancelled",
  expired: "Expired",
  checkout_abandoned: "Checkout Abandoned",
};

const STATUS_TONE: Record<string, string> = {
  pending_activation: "bg-amber-100 text-amber-900",
  active: "bg-emerald-100 text-emerald-900",
  cancelling: "bg-orange-100 text-orange-900",
  payment_failed: "bg-red-100 text-red-900",
  cancelled: "bg-muted text-foreground",
  expired: "bg-muted text-foreground",
  checkout_abandoned: "bg-orange-100 text-orange-900",
};

function deriveStatus(r: Row): string {
  if (
    r.checkout_abandoned_at &&
    !r.paid_at &&
    (r.status === "pending_activation" || r.status === "payment_failed")
  ) {
    return "checkout_abandoned";
  }
  // Still active today, but the member has asked to cancel. Showing a plain
  // "Active" badge here hides the cancellation until the day it takes effect,
  // so a scheduled cancellation gets its own status.
  if (
    r.cancel_at_period_end &&
    (r.status === "active" || r.status === "active_member")
  ) {
    return "cancelling";
  }
  return r.status;
}


/**
 * A row belongs in the members table only when the account has a real payment
 * relationship (paid now, or paid at some point and later cancelled/expired/
 * failed). Registered-but-never-paid signups stay on the platform for email
 * (waiting list / broadcasts) but are NOT members and are kept out of the
 * members table so the console reflects genuine membership only.
 */
function isGenuineMember(r: Row): boolean {
  return Boolean(
    r.paid_at ||
      r.stripe_subscription_id ||
      r.stripe_customer_id ||
      r.status === "active" ||
      r.status === "cancelled" ||
      r.status === "expired" ||
      r.status === "payment_failed",
  );
}

const CANCEL_REASON_LABELS: Record<string, string> = {
  too_expensive: "Too expensive",
  not_using_it: "Not using it enough",
  not_what_expected: "Not what they expected",
  found_alternative: "Found something else",
  technical_problems: "Technical problems",
  temporary_break: "Needed a break",
  other: "Something else",
  not_given: "No reason given",
};

function reasonLabel(reason: string | null | undefined) {
  if (!reason) return CANCEL_REASON_LABELS["not_given"]!;
  return CANCEL_REASON_LABELS[reason] ?? reason.replace(/_/g, " ");
}


function AdminMembershipPage() {
  const { loading: memLoading, isAdmin } = useMembership();
  const list = useServerFn(adminListMemberships);
  const cancel = useServerFn(adminCancelMembership);
  const portal = useServerFn(createCustomerPortalSession);
  const createCheckout = useServerFn(createFoundingCheckoutSession);
  const reconcile = useServerFn(reconcileMembershipTiers);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [mismatches, setMismatches] = useState<
    Awaited<ReturnType<typeof reconcileMembershipTiers>>["mismatches"] | null
  >(null);
  const [reconBusy, setReconBusy] = useState(false);
  const [showWaiting, setShowWaiting] = useState(false);
  const [showReasons, setShowReasons] = useState(false);


  // Jana's view: the members table shows only people who did NOT cancel.
  // Cancelled and cancel-scheduled members are counted but hidden below.
  const memberRows = rows === null
    ? null
    : rows.filter((r) => {
        if (!isGenuineMember(r)) return false;
        const status = deriveStatus(r);
        return status !== "cancelled" && status !== "cancelling" && status !== "expired";
      });
  const cancelledRows = rows === null
    ? []
    : rows.filter((r) => {
        if (!isGenuineMember(r)) return false;
        const status = deriveStatus(r);
        return status === "cancelled" || status === "cancelling" || status === "expired";
      });
  const waitingRows = rows === null ? [] : rows.filter((r) => !isGenuineMember(r));

  const reasonCounts = Object.entries(
    cancelledRows.reduce<Record<string, number>>((acc, r) => {
      const key = r.cancellation_reason ?? "not_given";
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {}),
  ).sort((a, b) => b[1] - a[1]);



  async function refresh() {
    setBusy(true);
    try {
      const r = await list({ data: undefined as never });
      setRows(r);
    } finally {
      setBusy(false);
    }
  }

  async function runReconciliation() {
    setReconBusy(true);
    try {
      const res = await reconcile();
      setMismatches(res.mismatches);
    } finally {
      setReconBusy(false);
    }
  }

  useEffect(() => {
    if (isAdmin) {
      refresh();
      runReconciliation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (memLoading) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-4xl items-center justify-center px-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <ShieldAlert className="mx-auto h-8 w-8 text-amber-600" />
        <p className="mt-3 text-sm">Admin access required.</p>
        <Link to="/" className="mt-4 inline-block text-sm underline">
          Back to home
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-serif text-2xl text-foreground">Membership console</h1>
        <button
          onClick={refresh}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-sm"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <MemberInsightsPanel />
      <CheckoutCampaignPanel />

      <div className="mb-6 rounded-2xl border border-border bg-card/60 p-4">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-medium">Tier reconciliation (read-only)</p>
          <button
            onClick={runReconciliation}
            disabled={reconBusy}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-xs"
          >
            <RefreshCw className={`h-3 w-3 ${reconBusy ? "animate-spin" : ""}`} />
            Re-run
          </button>
        </div>
        {mismatches === null ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : mismatches.length === 0 ? (
          <p className="inline-flex items-center gap-2 text-xs text-emerald-800">
            <CheckCircle2 className="h-4 w-4" />
            All profiles are in sync with their membership status.
          </p>
        ) : (
          <div>
            <p className="mb-2 inline-flex items-center gap-2 text-xs text-amber-900">
              <AlertTriangle className="h-4 w-4" />
              {mismatches.length} mismatch{mismatches.length === 1 ? "" : "es"} — the DB trigger will auto-fix new events; historic drift is listed below.
            </p>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-start uppercase text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1">Email</th>
                    <th className="px-2 py-1">Profile tier</th>
                    <th className="px-2 py-1">Membership status</th>
                    <th className="px-2 py-1">Expected tier</th>
                    <th className="px-2 py-1">Subscription</th>
                  </tr>
                </thead>
                <tbody>
                  {mismatches.map((m) => (
                    <tr key={m.user_id} className="border-t border-border/50">
                      <td className="px-2 py-1">{m.email ?? "—"}</td>
                      <td className="px-2 py-1">{m.profile_tier ?? "—"}</td>
                      <td className="px-2 py-1">{m.membership_status ?? "—"}</td>
                      <td className="px-2 py-1 font-medium">{m.expected_tier}</td>
                      <td className="px-2 py-1 text-muted-foreground"><code>{m.stripe_subscription_id ?? "—"}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="mb-6 rounded-2xl border border-border bg-card/60 p-4">
        <p className="mb-2 text-sm font-medium">Generate test checkout link</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="test@example.com (blank = you)"
            className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm"
          />
          <button
            className="btn-gold text-sm"
            onClick={async () => {
              const res = await createCheckout({
                data: testEmail ? { target_email: testEmail } : ({} as never),
              });
              if (res.ok === false) {
                alert(res.message);
              } else if (res.url) {
                window.open(res.url, "_blank", "noopener");
              }
              refresh();
            }}
          >
            Create checkout link
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-start text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Signed up</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Failed tries</th>
              <th className="px-3 py-2">Period end</th>
              <th className="px-3 py-2">Abandoned</th>
              <th className="px-3 py-2">Cancel@end</th>
              <th className="px-3 py-2">Subscription</th>
              <th className="px-3 py-2 text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {memberRows === null ? (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : memberRows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">
                  No memberships yet.
                </td>
              </tr>
            ) : (
              memberRows.map((r) => {
                const status = deriveStatus(r);
                return (
                <tr key={r.user_id} className="border-t border-border/50">
                  <td className="px-3 py-2">{r.email}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {r.activated_at ?? r.paid_at
                      ? new Date((r.activated_at ?? r.paid_at)!).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        STATUS_TONE[status] ?? "bg-muted"
                      }`}
                    >
                      {STATUS_LABEL[status] ?? status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {(r.failed_payment_count ?? 0) > 0 ? (
                      <div>
                        <span className="font-semibold text-red-700">
                          {r.failed_payment_count}× failed
                        </span>
                        <div className="text-muted-foreground">
                          {r.next_payment_retry_at
                            ? `next try ${new Date(r.next_payment_retry_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}`
                            : "no retry scheduled"}
                        </div>
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {r.current_period_end
                      ? new Date(r.current_period_end).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground" title={r.checkout_abandoned_at ?? ""}>
                    {r.checkout_abandoned_at
                      ? new Date(r.checkout_abandoned_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.cancel_at_period_end ? (
                      <span className="font-semibold text-orange-700">
                        Yes
                        {r.current_period_end
                          ? ` · ends ${new Date(r.current_period_end).toLocaleDateString()}`
                          : ""}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>

                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    <code>{r.stripe_subscription_id ?? "—"}</code>
                  </td>

                  <td className="px-3 py-2 text-end">
                    <div className="flex justify-end gap-1">
                      <button
                        disabled={!r.stripe_customer_id}
                        onClick={async () => {
                          const res = await portal({ data: { user_id: r.user_id } });
                          if (res.url) window.open(res.url, "_blank", "noopener");
                        }}
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs disabled:opacity-40"
                      >
                        Portal <ExternalLink className="h-3 w-3" />
                      </button>
                      <button
                        disabled={
                          !r.stripe_subscription_id ||
                          r.status === "cancelled" ||
                          r.status === "expired"
                        }
                        onClick={async () => {
                          if (!confirm(`Cancel subscription for ${r.email}?`)) return;
                          await cancel({
                            data: { user_id: r.user_id, immediate: true },
                          });
                          refresh();
                        }}
                        className="inline-flex items-center gap-1 rounded-md border border-red-300 bg-red-50 px-2 py-1 text-xs text-red-800 disabled:opacity-40"
                      >
                        <Ban className="h-3 w-3" /> Cancel
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })

            )}
          </tbody>
        </table>
      </div>

      {cancelledRows.length > 0 && (
        <div className="mt-3 rounded-2xl border border-dashed border-border bg-card/40 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium text-foreground">
                Why members left ({cancelledRows.length} cancelled or expiring, hidden above)
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Collected from the short question shown before cancellation is confirmed.
              </p>
            </div>
            <button
              onClick={() => setShowReasons((v) => !v)}
              className="rounded-full border border-border bg-background px-3 py-1 text-xs"
            >
              {showReasons ? "Hide" : "Show reasons"}
            </button>
          </div>
          <ul className="mt-3 flex flex-wrap gap-2 text-xs">
            {reasonCounts.map(([reason, count]) => (
              <li
                key={reason}
                className="rounded-full border border-border bg-background px-3 py-1 text-foreground"
              >
                {reasonLabel(reason)} · {count}
              </li>
            ))}
          </ul>
          {showReasons && (
            <ul className="mt-3 grid gap-2 text-xs text-muted-foreground">
              {cancelledRows.map((r) => (
                <li key={r.user_id}>
                  <span className="text-foreground">{r.email}</span> — {reasonLabel(r.cancellation_reason)}
                  {r.cancellation_reason_detail ? (
                    <p className="mt-0.5 italic">"{r.cancellation_reason_detail}"</p>
                  ) : null}
                </li>
              ))}
            </ul>

          )}
        </div>
      )}


      {waitingRows.length > 0 && (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-card/40 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium text-foreground">
                Waiting list — registered, never paid ({waitingRows.length})
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Kept on the platform for email and broadcasts only. Not members,
                no access to member content, excluded from the members table above.
              </p>
            </div>
            <button
              onClick={() => setShowWaiting((v) => !v)}
              className="rounded-full border border-border bg-background px-3 py-1 text-xs"
            >
              {showWaiting ? "Hide" : "Show emails"}
            </button>
          </div>
          {showWaiting && (
            <ul className="mt-3 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
              {waitingRows.map((r) => (
                <li key={r.user_id} className="truncate">
                  {r.email}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}



      <p className="mt-4 text-xs text-muted-foreground">
        Webhook events (<code>checkout.session.completed</code>,
        <code> customer.subscription.*</code>, <code>invoice.payment_*</code>) drive
        the status column. Refresh after test payments to see updates.
      </p>
    </div>
  );
}
